import { supabase } from "@/lib/supabaseClient";

/**
 * Normalise un email (trim + lowercase)
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Lie automatiquement les leads orphelins (user_id null) à un utilisateur
 * et infère role + status depuis le lead le plus récent.
 * 
 * @param userId - ID de l'utilisateur
 * @param email - Email de l'utilisateur
 * @returns {linked, inferredRole, inferredStatus}
 */
export async function linkOrphanLeadsToUser(
  userId: string,
  email: string
): Promise<{
  linked: number;
  inferredRole: string | null;
  inferredStatus: string | null;
}> {
  const e = normalizeEmail(email || "");
  if (!e) {
    return { linked: 0, inferredRole: null, inferredStatus: null };
  }

  try {
    // 1) Lire les leads orphelins (user_id null) avec cet email
    const { data: orphan, error: e1 } = await supabase
      .from("leads")
      .select("id,type,status,created_at")
      .eq("email", e)
      .is("user_id", null)
      .order("created_at", { ascending: false })
      .limit(50);

    if (e1) throw new Error("LEADS_ORPHAN_READ_FAILED");

    const ids = (orphan || []).map((x) => x.id);

    // 2) Lier ces leads à l'utilisateur
    if (ids.length > 0) {
      const { error: e2 } = await supabase
        .from("leads")
        .update({ user_id: userId })
        .in("id", ids);

      if (e2) throw new Error("LEADS_ORPHAN_LINK_FAILED");
    }

    // 3) Relire le lead le plus récent (désormais lié ou déjà lié)
    const { data: last, error: e3 } = await supabase
      .from("leads")
      .select("type,status,created_at")
      .or(`user_id.eq.${userId},email.eq.${e}`)
      .order("created_at", { ascending: false })
      .limit(1);

    if (e3) throw new Error("LEADS_READ_FAILED");

    const latest = (last || [])[0];
    const inferredRole =
      latest?.type === "AO" || latest?.type === "COMMERCE"
        ? latest.type
        : null;
    const inferredStatus =
      latest?.status === "FORM_SUBMITTED" ? "FORM_SUBMITTED" : null;

    // 4) Compléter le profil si role/status/email manquants
    const { data: prof, error: e4 } = await supabase
      .from("profiles")
      .select("role,status,email")
      .eq("user_id", userId)
      .maybeSingle();

    if (e4) throw new Error("PROFILE_READ_FAILED");

    const patch: Record<string, any> = {};
    if (!prof?.email) patch.email = e;
    if (!prof?.role && inferredRole) patch.role = inferredRole;
    if ((prof?.status === "NEW" || !prof?.status) && inferredStatus) {
      patch.status = inferredStatus;
    }

    if (Object.keys(patch).length > 0) {
      patch.updated_at = new Date().toISOString();
      const { error: e5 } = await supabase
        .from("profiles")
        .update(patch)
        .eq("user_id", userId);

      if (e5) throw new Error("PROFILE_PATCH_FAILED");
    }

    return { linked: ids.length, inferredRole, inferredStatus };
  } catch (error) {
    console.error("linkOrphanLeadsToUser error:", error);
    throw error;
  }
}